import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import TreasureCard, { Treasure } from '../components/TreasureCard';
import { UpgradeCard } from '../components/UpgradeCard';
import { TREASURES } from '../constants/treasures';
import { useLanguage } from '../i18n/LanguageContext';
type Props = { walletAddress: string };

type ActiveDeposit = {
  id: number;
  package_id: number;
  created_at: string;
  ready_at: string;
  expires_at: string;
  status: string;
  seconds_to_ready: number;
  seconds_to_expire: number;
  can_claim: boolean;
  payout_amount: number;
};

type PackageInfo = {
  id: number;
  name: string;
  amount: number;
  payout: number;
  roi: number;
  unlock_condition: string;
  is_unlocked: boolean;
  can_purchase: boolean;
  unlock_progress: number; // 0-3 claim'ов сделано
  total_earned: number; // Общий профит с этого сундука
  claims_count: number; // Количество собранных наград
};

type Upgrade = {
  id: number;
  name: string;
  description: string;
  boost_percent: number;
  price: number;
  is_bought: boolean;
  is_available: boolean;
};

type UpgradesData = {
  upgrades: Upgrade[];
  total_boost: number;
  user_balance: number;
};

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

const TreasuresScreen = ({ walletAddress }: Props) => {
  const [activeDeposits, setActiveDeposits] = useState<ActiveDeposit[]>([]);
  const [packages, setPackages] = useState<PackageInfo[]>([]);
  const [upgrades, setUpgrades] = useState<UpgradesData | null>(null);
  const [upgradesOpen, setUpgradesOpen] = useState<boolean>(false);
  const [segment, setSegment] = useState<'buy' | 'active'>('buy');
  const [faqOpen, setFaqOpen] = useState<boolean>(false);
  const [rulesOpen, setRulesOpen] = useState<boolean>(false);
  const treasures: Treasure[] = useMemo(() => TREASURES as unknown as Treasure[], []);
  const { t, language, setLanguage } = useLanguage();

  const loadPackages = async (retryCount = 3): Promise<void> => {
    try {
      console.log('📦 Загрузка информации о сундуках...', { walletAddress, retryCount });
      
      // ✅ ЧИТАЕМ referrer_id из URL или localStorage
      const webApp = (window as any)?.Telegram?.WebApp;
      let startParam = webApp?.initDataUnsafe?.start_param;
      
      if (!startParam && window.location.hash) {
        const match = window.location.hash.match(/tgWebAppStartParam=(\d+)/);
        if (match) {
          startParam = match[1];
        }
      }
      
      // Сохраняем в localStorage если есть
      if (startParam && !localStorage.getItem('referrer_id')) {
        localStorage.setItem('referrer_id', startParam);
        console.log('💾 referrer_id сохранен в localStorage:', startParam);
      }
      
      // Читаем из localStorage если не было в URL
      const referrerId = startParam || localStorage.getItem('referrer_id');
      
      const params: any = { wallet_address: walletAddress };
      if (referrerId) {
        params.referrer_id = parseInt(referrerId);
        console.log('🔗 Передаем referrer_id в /packages:', referrerId);
      }
      
      const res = await axios.get(`${API_BASE_URL}/packages`, { 
        params,
        timeout: 30000
      });
      console.log('✅ Информация о сундуках загружена:', res.data);
      setPackages(res.data);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки сундуков:', error);
      if (retryCount > 0 && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadPackages(retryCount - 1);
      }
    }
  };

  const loadActive = async (retryCount = 3): Promise<void> => {
    try {
      console.log('📡 Загрузка депозитов...', { walletAddress, retryCount });
      const res = await axios.get(`${API_BASE_URL}/user/deposits`, { 
        params: { wallet_address: walletAddress },
        timeout: 30000 // 30 секунд
      });
      console.log('✅ Депозиты загружены:', res.data);
      const all = res.data as any[];
      const act = all.filter(d => d.status === 'active').map(d => ({ 
        id: d.id, 
        package_id: d.package_id, 
        created_at: d.created_at,
        ready_at: d.ready_at,
        expires_at: d.expires_at,
        status: d.status,
        seconds_to_ready: d.seconds_to_ready || 0,
        seconds_to_expire: d.seconds_to_expire || 0,
        can_claim: d.can_claim || false,
        payout_amount: d.payout_amount || 0
      }));
      setActiveDeposits(act);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки депозитов:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        retryCount
      });
      
      // Retry при таймауте или сетевых ошибках
      if (retryCount > 0 && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
        console.log(`🔄 Повторная попытка через 2 сек... (осталось попыток: ${retryCount})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadActive(retryCount - 1);
      }
    }
  };

  const loadUpgrades = async () => {
    try {
      console.log('⚡ Загрузка улучшений...');
      const res = await axios.get(`${API_BASE_URL}/upgrades`, {
        params: { wallet_address: walletAddress },
        timeout: 30000
      });
      console.log('✅ Улучшения загружены:', res.data);
      setUpgrades(res.data);
    } catch (error: any) {
      console.error('❌ Ошибка загрузки улучшений:', error);
    }
  };

  useEffect(() => { 
    loadPackages();
    loadActive();
    loadUpgrades();
  }, [walletAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (pkgId: number) => activeDeposits.some(d => d.package_id === pkgId);
  
  const getActiveDeposit = (pkgId: number) => activeDeposits.find(x => x.package_id === pkgId);

  const handleBuy = async (pkgId: number) => {
    try {
      console.log('🛒 Покупка сундука:', pkgId);
      
      // Получаем referrer_id из localStorage (если есть)
      const referrerId = localStorage.getItem('referrer_id');
      const requestData: any = { 
        package_id: pkgId, 
        wallet_address: walletAddress 
      };
      if (referrerId) {
        requestData.referrer_id = parseInt(referrerId);
        console.log('🔗 Передаем referrer_id:', referrerId);
      }
      
      await axios.post(`${API_BASE_URL}/deposit`, requestData, {
        timeout: 30000
      });
      console.log('✅ Сундук куплен');
      await loadPackages(); // Обновляем информацию о разблокировке
      await loadActive();
      setSegment('active');
    } catch (e: any) {
      console.error('❌ Ошибка покупки:', e);
      const msg = e?.response?.data?.detail || 'Ошибка покупки';
      alert(msg);
    }
  };

  const handleClaim = async (depositId: number) => {
    try {
      console.log('🎁 Сбор награды:', depositId);
      await axios.post(`${API_BASE_URL}/claim`, { 
        deposit_id: depositId, 
        wallet_address: walletAddress 
      }, {
        timeout: 30000
      });
      console.log('✅ Награда получена');
      await loadPackages(); // Обновляем информацию о разблокировке
      await loadActive();
      alert('Награда успешно получена!');
    } catch (e: any) {
      console.error('❌ Ошибка сбора награды:', e);
      const msg = e?.response?.data?.detail || 'Ошибка получения награды';
      alert(msg);
    }
  };

  const handleBuyUpgrade = async (upgradeId: number) => {
    try {
      console.log('⚡ Покупка улучшения:', upgradeId);
      const res = await axios.post(`${API_BASE_URL}/upgrades/buy`, {
        wallet_address: walletAddress,
        upgrade_id: upgradeId
      }, {
        timeout: 30000
      });
      console.log('✅ Улучшение куплено:', res.data);
      alert(res.data.message);
      await loadUpgrades(); // Обновляем информацию об улучшениях
    } catch (e: any) {
      console.error('❌ Ошибка покупки улучшения:', e);
      const msg = e?.response?.data?.detail || 'Ошибка покупки улучшения';
      alert(msg);
    }
  };

  const Countdown: React.FC<{ initialSeconds: number; onComplete?: () => void }> = ({ initialSeconds, onComplete }) => {
    const [left, setLeft] = useState<number>(initialSeconds);
    useEffect(() => {
      setLeft(initialSeconds);
    }, [initialSeconds]);
    
    useEffect(() => {
      if (left <= 0) {
        if (onComplete) onComplete();
        return;
      }
      const id = setInterval(() => setLeft(prev => {
        const newVal = Math.max(0, prev - 1);
        if (newVal === 0 && onComplete) onComplete();
        return newVal;
      }), 1000);
      return () => clearInterval(id);
    }, [left, onComplete]);
    
    const h = Math.floor(left / 3600).toString().padStart(2, '0');
    const m = Math.floor((left % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(left % 60).toString().padStart(2, '0');
    
    if (left === 0) {
      return (
        <div className="w-full py-3 text-center text-green-400 text-lg bg-green-500/20 rounded-lg mb-2 font-bold">
          {t.readyToClaim}
        </div>
      );
    }
    
    return (
      <div className="w-full py-3 text-center text-white/80 text-lg bg-black/20 rounded-lg mb-2">
        {h}:{m}:{s}
      </div>
    );
  };

  // Получить информацию о сундуке из API
  const getPackageInfo = (id: number): PackageInfo | undefined => {
    return packages.find(p => p.id === id);
  };

  // Проверка разблокировки (из API)
  const isUnlocked = (id: number): boolean => {
    const pkg = getPackageInfo(id);
    return pkg?.is_unlocked || false;
  };

  // Получить прогресс разблокировки (0-3 claim'ов)
  const getUnlockProgress = (id: number): number => {
    const pkg = getPackageInfo(id);
    return pkg?.unlock_progress || 0;
  };

  return (
    <div
      className="pb-20 px-3 min-h-screen bg-cover bg-center bg-gradient-to-br from-indigo-500 to-purple-600"
      style={{ backgroundImage: 'url(/assets/treasures-bg.jpg)' }}
    >
      <div className="text-white">
        <div className="rounded-2xl bg-white/10 border border-white/20 p-4 shadow-soft">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xl font-bold">{t.treasureTrials}</div>
            <div className="flex gap-2">
              <button
                onClick={() => setLanguage(language === 'ru' ? 'en' : 'ru')}
                className="px-3 py-2 rounded-lg font-semibold bg-purple-600 hover:bg-purple-700 transition-colors text-sm"
              >
                {language === 'ru' ? '🇬🇧 EN' : '🇷🇺 RU'}
              </button>
              <button
                onClick={() => setRulesOpen(true)}
                className="px-4 py-2 rounded-lg font-semibold bg-green-600 hover:bg-green-700 transition-colors text-sm"
              >
                {t.rulesButton}
              </button>
              <button
                onClick={() => setFaqOpen(true)}
                className="px-4 py-2 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 transition-colors text-sm"
              >
                {t.faqButton}
              </button>
            </div>
          </div>
          <div className="text-sm opacity-80">{t.treasureDescription}</div>
        </div>

        <div className="flex mt-4 rounded-xl overflow-hidden border border-white/20">
          <button
            className={`flex-1 py-2 font-semibold ${segment === 'buy' ? 'bg-white/10 text-yellow-400' : 'bg-transparent text-white/70'}`}
            onClick={() => setSegment('buy')}
          >
            {t.canBuyTab}
          </button>
          <button
            className={`flex-1 py-2 font-semibold ${segment === 'active' ? 'bg-white/10 text-yellow-400' : 'bg-transparent text-white/70'}`}
            onClick={() => setSegment('active')}
          >
            {t.activeTab}
          </button>
        </div>

        {/* Кнопка "Улучшения" - показывается только в режиме покупки если есть улучшения */}
        {segment === 'buy' && upgrades && upgrades.upgrades.length > 0 && (
          <button
            className="w-full mt-3 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-between"
            onClick={() => setUpgradesOpen(!upgradesOpen)}
          >
            <span className="flex items-center gap-2">
              <span>⚡</span>
              <span>Улучшения</span>
              {upgrades.total_boost > 0 && (
                <span className="text-yellow-400 text-sm">(-{upgrades.total_boost}%)</span>
              )}
            </span>
            <span className="text-xl">{upgradesOpen ? '▼' : '▶'}</span>
          </button>
        )}
      </div>

      {/* Секция улучшений - показывается только когда открыта */}
      {segment === 'buy' && upgrades && upgrades.upgrades.length > 0 && upgradesOpen && (
        <div className="mt-4 rounded-2xl bg-white/10 border border-white/20 p-4 shadow-soft">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Улучшения</h2>
            {upgrades.total_boost > 0 && (
              <div className="text-right">
                <div className="text-sm text-gray-300">Ваше ускорение:</div>
                <div className="text-2xl font-bold text-yellow-400">-{upgrades.total_boost}%</div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {upgrades.upgrades.map((upgrade) => (
              <UpgradeCard
                key={upgrade.id}
                name={upgrade.name}
                description={upgrade.description}
                price={upgrade.price}
                boostPercent={upgrade.boost_percent}
                isBought={upgrade.is_bought}
                isAvailable={upgrade.is_available}
                userBalance={upgrades.user_balance}
                onBuy={() => handleBuyUpgrade(upgrade.id)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {segment === 'buy' && (
          <>
            {treasures.filter(treasure => !isActive(treasure.id)).map((treasure, index, filteredArray) => {
              const unlocked = isUnlocked(treasure.id);
              const unlockProgress = getUnlockProgress(treasure.id);
              const packageInfo = getPackageInfo(treasure.id);
              const isInDevelopment = treasure.id >= 13;
              
              // Определяем следующий к разблокировке сундук
              const isNextToUnlock = (() => {
                if (unlocked || isInDevelopment) return false;
                
                // Ищем последний разблокированный сундук (в filteredArray)
                const lastUnlockedId = filteredArray
                  .map(t => t.id)
                  .filter(id => id <= 11 && isUnlocked(id))
                  .sort((a, b) => b - a)[0] || 0;
                
                // Ищем последний активный сундук (в ПОЛНОМ массиве treasures, т.к. filteredArray уже отфильтрован по !isActive)
                const lastActiveId = treasures
                  .map(t => t.id)
                  .filter(id => id <= 11 && isActive(id))
                  .sort((a, b) => b - a)[0] || 0;
                
                // Следующий к разблокировке = max(lastUnlockedId, lastActiveId) + 1
                const maxId = Math.max(lastUnlockedId, lastActiveId);
                return treasure.id === (maxId || 1) + 1;
              })();
              
              // Рассчитываем прозрачность
              // Яркие (100%): разблокированные + следующий к разблокировке
              // Затемнённые (30%): остальные закрытые
              // Сильно затемнённые (10%): в разработке
              const opacity = isInDevelopment ? 0.1 : (unlocked || isNextToUnlock ? 1 : 0.30);
              
              // Найдем предыдущий сундук для отображения его названия
              const previousTreasure = treasure.id > 1 ? treasures.find(t => t.id === treasure.id - 1) : undefined;
              
              return (
                <div 
                  key={treasure.id} 
                  className="relative"
                >
                  {/* Карточка сундука */}
                  <div style={{ opacity }}>
                    <TreasureCard 
                      treasure={{ 
                        ...treasure, 
                        unlocked, 
                        unlockProgress,
                        earnedTon: packageInfo?.total_earned || 0,
                        claimsCount: packageInfo?.claims_count || 0
                      }} 
                      onBuy={() => handleBuy(treasure.id)}
                      previousTreasureName={previousTreasure?.name}
                    />
                  </div>
                  
                  {/* "Новые сундуки скоро в наличии" для сундука 10 - ЧЕТКАЯ НАДПИСЬ */}
                  {isInDevelopment && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg pointer-events-none">
                      <div className="text-white text-xl font-bold text-center whitespace-pre-line">{t.comingSoonChests}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
        {segment === 'active' && (
          <>
            {treasures.filter(treasure => isActive(treasure.id)).length === 0 && (
              <div className="text-center font-bold text-white/90">{t.noActiveChests}</div>
            )}
            {treasures.filter(treasure => isActive(treasure.id)).map((treasure) => {
              const deposit = getActiveDeposit(treasure.id);
              if (!deposit) return null;
              
              const isReady = deposit.can_claim;
              const isWaiting = deposit.seconds_to_ready > 0;
              const packageInfo = getPackageInfo(treasure.id);
              
              return (
                <div key={`active-${treasure.id}`} className="card relative overflow-hidden">
                  {/* Статус и таймеры */}
                  {isWaiting && (
                    <>
                      <div className="text-center text-yellow-400 text-sm mb-2 font-semibold">
                        {t.chestOpening}
                      </div>
                      <Countdown initialSeconds={deposit.seconds_to_ready} onComplete={() => loadActive()} />
                    </>
                  )}
                  
                  {isReady && (
                    <>
                      <div className="text-center text-green-400 text-sm mb-2 font-semibold animate-pulse">
                        {t.rewardReady}
                      </div>
                      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-3 mb-2 border border-yellow-500/50">
                        <div className="text-center text-white/90 text-xs mb-1">
                          {t.claimWindowExpires}
                        </div>
                        <Countdown initialSeconds={deposit.seconds_to_expire} onComplete={() => loadActive()} />
                        <div className="text-center text-red-400 text-xs mt-1">
                          {t.afterExpiryReturn}
                        </div>
                      </div>
                    </>
                  )}
                  
                  <TreasureCard 
                    treasure={{ 
                      ...treasure, 
                      earnedTon: packageInfo?.total_earned || 0,
                      claimsCount: packageInfo?.claims_count || 0
                    }} 
                    onBuy={undefined} 
                    hideAction 
                  />
                  
                  {/* Информация о выплате */}
                  <div className="mt-2 bg-white/10 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-900 font-semibold">{t.rewardOnClaim}</div>
                    <div className="text-lg font-bold text-green-400">{deposit.payout_amount.toFixed(2)} TON</div>
                  </div>
                  
                  {/* Кнопка сбора */}
                  <button 
                    className={`mt-2 w-full py-3 rounded-lg font-semibold transition-all ${
                      isReady
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white cursor-pointer shadow-lg' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={!isReady}
                    onClick={() => isReady && handleClaim(deposit.id)}
                  >
                    {isReady ? t.claimRewardButton : t.waitingForOpening}
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Rules Modal */}
      {rulesOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={() => setRulesOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 to-gray-800 border border-white/20 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-6 text-center text-green-400">{t.rulesTitle}</h2>
              
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-bold mb-2">{t.rulesHowItWorks}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.rulesHowItWorksText}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold mb-2">{t.rulesUnlocking}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.rulesUnlockingText}
                  </p>
                </div>

                <div className="bg-red-900/30 border-2 border-red-500/50 rounded-lg p-4">
                  <h3 className="text-lg font-bold mb-2 text-red-400">{t.rulesBurning}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.rulesBurningText}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold mb-2">{t.rulesImportant}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.rulesImportantText}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setRulesOpen(false)}
                className="w-full mt-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition-colors"
              >
                {t.rulesClose}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Modal */}
      {faqOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={() => setFaqOpen(false)}
        >
          <div
            className="bg-gradient-to-br from-gray-900 to-gray-800 border border-white/20 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-6 text-center text-yellow-400">{t.faqTitle}</h2>
              
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-bold mb-2">{t.faqAccountRules}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.faqAccountText}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold mb-2">{t.faqWithdrawal}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.faqWithdrawalText}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold mb-2">{t.faqDisclaimer}</h3>
                  <p className="text-sm opacity-90 leading-relaxed whitespace-pre-line">
                    {t.faqDisclaimerText}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setFaqOpen(false)}
                className="w-full mt-6 py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                {t.faqClose}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasuresScreen;
